import React, { useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';

const AccountResults = ({
  results = [],
  isLoading = false,
  searchQuery = '',
  error = null,
  status = 'Idle',
  onNewSearch = () => {},
  showConfidence = true
}) => {
  const [copiedIndex, setCopiedIndex] = useState(null);
  const [showResults, setShowResults] = useState(true);
  const [showHashInfo, setShowHashInfo] = useState(false);

  useEffect(() => {
      if (results.length > 0 || isLoading || error || (searchQuery && status !== 'Idle' && status !== 'Initiating...')) {
          setShowResults(false);
          const timeout = setTimeout(() => setShowResults(true), 50);
          return () => clearTimeout(timeout);
      }
  }, [results, isLoading, error, searchQuery, status]);

  // Check if any result contains a hash
  useEffect(() => {
      const hasHashedPasswords = results.some(result => 
          result?.password && (
              result.password.includes('$') || 
              result.password.length === 32 || // MD5
              result.password.length === 40 || // SHA1
              result.password.length === 64 || // SHA256
              result.password.length === 128 // SHA512
          )
      );
      setShowHashInfo(hasHashedPasswords);
  }, [results]);

  const getConfidenceClass = (confidence) => {
      const lowerConfidence = confidence?.toLowerCase();
      switch (lowerConfidence) {
          case 'high': return 'border-accent-green/30 hover:border-accent-green/50';
          case 'medium': return 'border-accent-blue/30 hover:border-accent-blue/50';
          case 'low': return 'border-accent-orange/30 hover:border-accent-orange/50';
          default: return 'border-accent-red/30 hover:border-accent-red/50';
      }
  };

  const getConfidenceAccentColor = (confidence) => {
       const lowerConfidence = confidence?.toLowerCase();
       switch (lowerConfidence) {
           case 'high': return 'accent-green';
           case 'medium': return 'accent-blue';
           case 'low': return 'accent-orange';
           default: return 'accent-red';
       }
   };

  const handleCopy = (text, index) => {
      navigator.clipboard.writeText(text).then(() => {
          setCopiedIndex(index);
          setTimeout(() => setCopiedIndex(null), 2000);
      });
  };

  // Hash Info Component
  const HashInfoBanner = () => (
      <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -10 }}
          className="bg-accent-blue/10 border border-accent-blue/30 rounded-xl p-4 mb-4 flex items-start gap-3"
      >
          <div className="flex-shrink-0 w-8 h-8 rounded-lg bg-accent-blue/20 flex items-center justify-center">
              <i className="fas fa-info-circle text-accent-blue"></i>
          </div>
          <div className="flex-1">
              <h4 className="text-sm font-semibold text-white mb-1">Password Hashes Detected</h4>
              <p className="text-xs text-gray-300 mb-2">
                  Some passwords are displayed as hashes (e.g., sha$5, md5:abcdef). These are encrypted passwords that need to be decrypted.
              </p>
              <a 
                  href="https://hashes.com" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 text-xs text-accent-blue hover:text-accent-blue/80 transition-colors"
              >
                  <i className="fas fa-external-link-alt"></i>
                  Decrypt hashes at hashes.com
              </a>
          </div>
          <button
              onClick={() => setShowHashInfo(false)}
              className="flex-shrink-0 w-6 h-6 rounded-lg hover:bg-dark-500 flex items-center justify-center transition-colors"
              title="Dismiss"
          >
              <i className="fas fa-times text-gray-400 text-xs"></i>
          </button>
      </motion.div>
  );

  // Check if password looks like a hash
  const isPasswordHash = (password) => {
      if (!password) return false;
      return (
          password.includes('$') || 
          password.match(/^[a-f0-9]{32}$/i) || // MD5
          password.match(/^[a-f0-9]{40}$/i) || // SHA1
          password.match(/^[a-f0-9]{64}$/i) || // SHA256
          password.match(/^[a-f0-9]{128}$/i) // SHA512
      );
  };

  // Rendering Logic for Different States
  const getResultState = () => {
      // 1. Error State
      if (error) {
          return (
              <motion.div
                  key="error" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.2 }}
                  className={`text-center py-10 transition-opacity duration-300 ${showResults ? 'opacity-100' : 'opacity-0'}`}
              >
                  <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-dark-500 flex items-center justify-center border-2 border-accent-red/50">
                      <i className="fas fa-exclamation-triangle text-accent-red text-3xl"></i>
                  </div>
                  <p className="text-gray-300 font-medium">Search Error</p>
                  <p className="text-gray-400 text-sm mt-2">{error}</p>
                  <button
                      onClick={onNewSearch}
                      className="mt-6 px-4 py-2 bg-dark-500 hover:bg-dark-400 rounded-xl text-sm transition-medium transform-gpu hover:-translate-y-0.5 hover:shadow-md"
                  >
                      Try Another Search
                  </button>
              </motion.div>
          );
      }

      // 2. Loading State
      if (isLoading) {
          return (
              <motion.div
                  key="loading" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.2 }}
                  className={`text-center py-10 transition-opacity duration-300 ${showResults ? 'opacity-100' : 'opacity-0'}`}
              >
                  <div className="w-20 h-20 mx-auto mb-4 relative">
                      <div className="w-full h-full rounded-full border-4 border-accent-green/30 border-t-accent-green animate-spin"></div>
                      <div className="absolute inset-0 flex items-center justify-center">
                          <i className="fas fa-user-shield text-accent-green text-xl"></i>
                      </div>
                  </div>
                  <p className="text-gray-400 mt-2">
                      Searching for <span className="font-bold text-white">{searchQuery}</span>...
                  </p>
                  {status && status !== 'Initiating...' && status !== 'Idle' && (
                       <div className="flex justify-center gap-2 text-xs text-gray-500 mt-2 animate-pulse-slow">
                          <div className="px-2 py-1 bg-dark-500 rounded-lg ">{status}</div>
                       </div>
                  )}
              </motion.div>
          );
      }

      // 3. No Results State
      if (results.length === 0 && searchQuery && status !== 'Idle' && status !== 'Initiating...' && !error && !isLoading) {
          return (
              <motion.div
                  key="no-results" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.2 }}
                  className={`text-center py-10 transition-opacity duration-300 ${showResults ? 'opacity-100' : 'opacity-0'}`}
              >
                  <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-dark-500 flex items-center justify-center">
                      <i className="fas fa-times-circle text-accent-red text-3xl"></i>
                  </div>
                  <p className="text-gray-400">No results found for "<span className="font-bold text-white">{searchQuery}</span>"</p>
                  <p className="text-xs text-gray-500 mt-3">Try a different username or adjust search options</p>
                  <button
                      onClick={onNewSearch}
                      className="mt-4 px-4 py-2 bg-dark-500 hover:bg-dark-400 rounded-xl text-sm transition-medium transform-gpu hover:-translate-y-0.5 hover:shadow-md"
                  >
                      Try Another Search
                  </button>
              </motion.div>
          );
      }

      // 4. Idle State
      if (results.length === 0 && !searchQuery && status === 'Idle' && !isLoading && !error) {
          return (
              <motion.div
                  key="idle" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.2 }}
                  className={`text-center py-10 transition-opacity duration-300 ${showResults ? 'opacity-100' : 'opacity-0'}`}
              >
                  <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-dark-500 flex items-center justify-center">
                      <i className="fas fa-search text-gray-400 text-3xl"></i>
                  </div>
                  <p className="text-gray-400">Enter a username to start searching</p>
                  <div className="mt-4 space-y-2 max-w-xs mx-auto">
                      <p className="text-xs text-gray-500">Popular searches:</p>
                      <div className="flex flex-wrap justify-center gap-2">
                          {['Dream', 'Technoblade', 'Notch', 'jeb_', 'Sapnap'].map((name, i) => (
                              <button
                                  key={i}
                                  className="px-3 py-1 bg-dark-500 hover:bg-dark-400 rounded-lg text-xs transition-medium hover:text-accent-green"
                                  onClick={() => onNewSearch && onNewSearch(name)}
                              >
                                  {name}
                              </button>
                          ))}
                      </div>
                  </div>
              </motion.div>
          );
      }

      // 5. Results State
      if (results.length > 0) {
          return (
               <motion.div
                    key="results-list" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.2 }}
                    className={`transition-opacity duration-300 ${showResults ? 'opacity-100' : 'opacity-0'}`}
                >
                  {/* Show Hash Info Banner if needed */}
                  <AnimatePresence>
                      {showHashInfo && <HashInfoBanner />}
                  </AnimatePresence>
                  
                  {/* Results List */}
                  <div className="space-y-3">
                      {results.map((result, index) => {
                        if (!result) {
                            return null;
                        }
                         const accentColor = getConfidenceAccentColor(result.confidence);
                         const borderColorClass = getConfidenceClass(result.confidence);
                         const passwordIsHash = isPasswordHash(result.password);

                         return (
                              <motion.div
                                  key={result.id || index}
                                  layout
                                  initial={{ opacity: 0, y: 10 }}
                                  animate={{ opacity: 1, y: 0 }}
                                  transition={{ duration: 0.2, delay: index * 0.05 }}
                                  className={`bg-dark-500 rounded-xl p-4 border hover:shadow-md transition-all duration-300 transform-gpu cursor-default ${borderColorClass} hover:-translate-y-0.5 hover:border-opacity-60`}
                              >
                                  <div className="flex items-start sm:items-center flex-col sm:flex-row">
                                      {/* Avatar */}
                                      <div className="w-12 h-12 rounded-lg bg-dark-400 overflow-hidden mr-4 mb-3 sm:mb-0 flex-shrink-0 transition-medium transform-gpu hover:scale-110">
                                          <img
                                              src={result.avatar || `https://minotar.net/avatar/${result.username}/48`}
                                              alt={`${result.username}'s Avatar`}
                                              className="w-full h-full object-cover minecraft-avatar"
                                              onError={(e) => { e.target.onerror = null; e.target.src='https://minotar.net/avatar/steve/48'; }}
                                          />
                                      </div>
                                      {/* Main Content */}
                                      <div className="flex-1 w-full">
                                          <div className="flex justify-between items-start mb-2">
                                              {/* Username & Type */}
                                              <div>
                                                  <div className="font-bold text-white">{result.username}</div>
                                                  <div className="text-xs text-gray-400">{result.type || 'Unknown Type'}</div>
                                              </div>
                                              {/* Confidence Badge */}
                                              {showConfidence && result.confidence && (
                                                  <span className={`bg-${accentColor} bg-opacity-20 text-${accentColor} text-xs px-2 py-0.5 rounded font-medium whitespace-nowrap`}>
                                                      {result.confidence} Confidence
                                                  </span>
                                              )}
                                          </div>
                                          {/* Credentials & Copy Button */}
                                          <div className="flex items-center">
                                              <div className="font-mono text-sm bg-dark-600 px-2 py-1 rounded flex-1 mr-2 border border-dark-400 hover:border-dark-300 transition-medium break-all">
                                                  <span className={`text-${accentColor}`}>{result.username}:</span>
                                                  <span className="text-gray-300">{result.password || '**********'}</span>
                                                  {passwordIsHash && (
                                                                                                            <span className="ml-2 text-xs text-accent-blue" title="This is a hashed password">
                                                          <i className="fas fa-lock"></i> Hash
                                                      </span>
                                                  )}
                                              </div>
                                              <button
                                                  title="Copy Credentials"
                                                  className={`p-2 w-9 h-9 flex items-center justify-center rounded transition-medium transform-gpu ${
                                                      copiedIndex === index
                                                          ? `bg-${accentColor}/20 text-${accentColor} scale-110`
                                                          : 'bg-dark-400 hover:bg-dark-300 text-gray-300 hover:scale-110'
                                                  }`}
                                                  onClick={(e) => {
                                                      e.stopPropagation();
                                                      handleCopy(`${result.username}:${result.password}`, index);
                                                  }}
                                              >
                                                  {copiedIndex === index ? <i className="fas fa-check"></i> : <i className="fas fa-copy"></i>}
                                              </button>
                                          </div>
                                          {/* Source & Last Verified */}
                                          {(result.source || result.lastVerified) && (
                                              <div className="mt-2 flex flex-col sm:flex-row justify-between text-xs text-gray-400">
                                                  {result.source && <span>Source: <span className="text-gray-300">{result.source}</span></span>}
                                                  {result.lastVerified && <span className="sm:ml-4">Last Verified: <span className="text-gray-300">{result.lastVerified}</span></span>}
                                              </div>
                                          )}
                                      </div>
                                  </div>
                              </motion.div>
                         );
                       })}
                  </div>
               </motion.div>
          );
      }

      // Fallback
      return <motion.div key="fallback" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.2 }} className="text-center py-10 text-gray-500">Preparing search results area...</motion.div>;
  };

  return (
      <div className="lg:col-span-2 bg-dark-600 rounded-2xl p-6 hover:shadow-md transition-medium transform-gpu hover:scale-[1.005] flex flex-col h-full">
          {/* Header - Keep as flex-shrink-0 */}
          <div className="flex-shrink-0">
              <h3 className="font-bold text-lg mb-5 flex items-center group">
                  <i className="fas fa-search mr-2 text-accent-green group-hover:rotate-12 transition-medium"></i>
                  Account Search Results
                  {!isLoading && results.length > 0 && (
                      <span className="ml-3 px-2 py-0.5 bg-accent-green bg-opacity-20 text-accent-green rounded-lg text-xs animate-pulse-slow">
                          {results.length} matches found
                      </span>
                  )}
                  {isLoading && (
                      <span className="ml-3 text-xs text-gray-400 animate-pulse">
                          ({status || 'Searching'}...)
                      </span>
                  )}
                  {error && (
                      <span className="ml-3 text-xs text-accent-red">
                          (Error)
                      </span>
                  )}
              </h3>
          </div>

          {/* Scrollable results area - Use flex-1 to take available space */}
          <div className="flex-1 overflow-y-auto custom-scrollbar pr-2 relative mb-4">
              <AnimatePresence mode="wait">
                  {getResultState()}
              </AnimatePresence>
          </div>

          {/* Footer buttons - Keep as flex-shrink-0 */}
          {results.length > 0 && !isLoading && (
              <div className="flex-shrink-0 flex gap-3 mt-auto">
                  <button className="px-4 py-2 bg-dark-400 hover:bg-dark-300 rounded-xl text-sm font-medium transition-medium transform-gpu hover:-translate-y-0.5 hover:shadow-md active:translate-y-0 active:shadow-inner flex-1">
                      <i className="fas fa-file-export mr-1"></i> Export Results
                  </button>
                  <button
                      className="px-4 py-2 bg-gradient-to-r from-accent-green to-accent-blue text-dark-800 rounded-xl text-sm font-medium transition-medium transform-gpu hover:-translate-y-0.5 hover:shadow-md hover:shadow-accent-green/20 active:translate-y-0 active:shadow-inner flex-1"
                      onClick={onNewSearch}
                  >
                      <i className="fas fa-search mr-1"></i> New Search
                  </button>
              </div>
          )}
      </div>
    );
};

export default AccountResults;